import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle,
  Bus,
  CarFront,
  Clock,
  Home,
  Plus,
  RotateCcw,
  Ticket,
  Timer,
  UserRound,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { formatFlightTime } from '@/lib/disruptions/airport-tz';
import type { DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { useCancelTrip, useCaseTrips, useDispatchTrips } from '@/lib/transport/use-trips';
import { liveTripFor, splitTripsForGroup } from '@/lib/transport/trips';
import {
  TRIP_DISPATCH_MODE_LABEL_KEY,
  TRIP_LEG_LABEL_KEY,
  TRIP_STATUS_PRESENTATION,
  isCancellable,
  isRedispatchable,
  isTerminalTripStatus,
  type TripLeg,
  type TripView,
} from '@/lib/transport/types';
import {
  TransportDispatchDialog,
  type DispatchDialogSubmit,
} from '@/components/pnr/transport-dispatch-dialog';
import { TransportCancelDialog } from '@/components/pnr/transport-cancel-dialog';
import { TransportVoucherSection } from '@/components/pnr/transport-voucher-section';
import { useGroupVoucher } from '@/lib/transport/use-voucher';
import { rosterVoucherCounts, voucherCoversRide, voucherPassengers } from '@/lib/transport/voucher-coverage';
import { providerLabel } from '@/lib/transport/providers';
import type { TransportDestination } from '@/lib/transport/destination';
import { useTransportMode } from '@/lib/transport/use-transport-mode';
import { routingDisruptionUrn, type PnrGroupScope } from '@/lib/pnr/scope';
import { cn } from '@/lib/cn';
import { FIELD_LABEL_CLASS } from './airport-tokens';
import { towerPath } from '@/routes/disruptions/operar/operate-types';

// Ground transport for one PNR group (feat/transport slice T5, NEX-439).
//
// Was an info-only row ("estimated in the fare, not separately bookable"). It is
// now the operator's ground-transport surface for this PNR: the group's live trips,
// MANUAL dispatch (group-wide or per passenger), cancel, and re-dispatch on a
// failure. The estimate it used to show survives as a footnote — it is a different
// number (a rebooking fare component, not what the rides actually cost).
//
// LIVE, NOT POLLED. One seeding read, then `TripChanged@v1` pushes through
// `applyEvent` into `['trips', caseUrn]` — no timer, no polling interval anywhere
// in this tree (frontend-conventions §15). A dropped socket is handled in
// `useCaseTrips` — re-join + ONE catch-up read on the reconnect EVENT — and the
// app-wide `ReconnectionBanner` already tells the operator the channel is down,
// so this card deliberately does not say it twice.
//
// NO DESTINATION, NO DISPATCH. A ride needs somewhere to go: a CONFIRMED hotel or
// the declared stay-home (`transportDestination`). Until then the card offers no
// dispatch, voucher, estimate or desk jump — only the rides that already exist
// (still cancellable) and why nothing else is on offer. What happens once the
// hotel IS confirmed is the governing policy's `transportMode`, never assumed.
//
// PII: the name on a trip row is the roster's ALREADY-MASKED `fullName`, matched
// locally by `passengerUrn`. No reveal control here — the audited reveal lives on
// PassengersCard (feedback_pii_reveal_model).

/** What the operator asked for when they opened the dispatch dialog. */
interface DispatchIntent {
  /** Pre-selected passengers; empty ⇒ the whole group. */
  passengerUrns: string[];
  leg: TripLeg | null;
  /** Distinguishes one open from the next so the dialog remounts with fresh state. */
  key: string;
}

export function TransportCard({
  group,
  scope,
  transportPriceUsd,
  hasSelected,
  destination,
  hotelPending = false,
}: {
  /** The PNR group this drawer is showing — the trips are sliced to its passengers. */
  group: DisruptionPnrGroupRow;
  /** The operation center's scope — resolves the disruption URN a MANUAL
   *  dispatch is stamped with, so the trip shows in the Transport desk. */
  scope: PnrGroupScope;
  /** The selected option's `priceBreakdown.transport` (USD, the airline's price); `null`
   *  when the price is not available, absent when there is no selected option. */
  transportPriceUsd?: number | null | undefined;
  /** A re-accommodation option IS chosen (drives the estimate copy). */
  hasSelected: boolean;
  /** Where a ride would go — `null` = nowhere defined yet, nothing to dispatch. */
  destination: TransportDestination;
  /** A room exists but is not confirmed yet (held for the passenger's answer). */
  hotelPending?: boolean;
}) {
  const { t } = useTranslation('pnrActions');
  const canDispatch = destination !== null;
  // Only the hotel leg has an AUTOMATIC path — a ride home is always manual.
  const transportMode = useTransportMode(group.origin, destination === 'HOTEL');
  const tripsQuery = useCaseTrips(group.caseUrn);
  const dispatch = useDispatchTrips();
  const cancel = useCancelTrip();

  const [dispatchIntent, setDispatchIntent] = useState<DispatchIntent | null>(null);
  const [cancelTarget, setCancelTarget] = useState<TripView | null>(null);
  const [dispatchError, setDispatchError] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // The group's Uber voucher (same cache entry the voucher section reads). A voucher IS
  // ground transport — the passenger rides whenever they want — so a passenger holding a
  // live code is not "without transport", and a failed car it stands in for is history.
  // Until the read lands, the roster's `transportVoucher` fact speaks. No destination,
  // no voucher (it is issued to a confirmed hotel) — so no read either.
  const voucher = useGroupVoucher(group.caseUrn, canDispatch ? group.subCaseUrn : undefined).data;
  const carried = useMemo((): ReadonlySet<string> => {
    if (voucher !== undefined) return voucherPassengers(voucher);
    const fact = group.transportVoucher;
    if (!rosterVoucherCounts(fact)) return new Set();
    return new Set(fact.paxUrns.length > 0 ? fact.paxUrns : group.passengers.map((p) => p.paxUrn));
  }, [voucher, group.transportVoucher, group.passengers]);

  const trips = tripsQuery.data;
  const { mine, others } = useMemo(
    () =>
      splitTripsForGroup(trips, {
        subCaseUrn: group.subCaseUrn,
        pnrLabel: group.pnrLabel,
        pnrUrn: group.pnrUrn,
        paxUrns: group.passengers.map((p) => p.paxUrn),
      }),
    [trips, group.subCaseUrn, group.pnrLabel, group.pnrUrn, group.passengers],
  );

  /** paxUrn → the masked roster name, so a trip row reads as a person. */
  const nameOf = useMemo(() => {
    const byUrn = new Map(group.passengers.map((p) => [p.paxUrn, p.fullName]));
    return (paxUrn: string): string => byUrn.get(paxUrn) ?? shortUrn(paxUrn);
  }, [group.passengers]);

  // Passengers with nothing coming for them — the per-passenger dispatch affordance.
  // A passenger who already has a live trip, or a voucher code, is not offered another
  // vehicle; an infant rides on the lap of a voucher holder (infants get no code).
  const withoutTransport = group.passengers.filter(
    (p) =>
      !liveTripFor(mine, p.paxUrn) &&
      !carried.has(p.paxUrn) &&
      !(carried.size > 0 && isInfant(p.paxType)),
  );
  /** The voucher pays this ride already — a car on top is the double spend it replaced. */
  const voucherCovers = (trip: TripView): boolean => voucherCoversRide(voucher, trip.leg, trip.passengerUrn);

  // The ride HOME (ride-home vertical): a stay-home (`LOCAL_RESIDENT`) group whose
  // declaration carried an address the geocoder could place. Gated on the POINT,
  // not the declaration — a `geocodeError` row never offers a ride the vendor
  // would fail (Uber refuses to fabricate a drop-off). Per-passenger CAR from the
  // MANUAL door only; no AUTOMATIC trigger, no return leg, never a shuttle seat.
  const homeDropoff =
    group.passengerElection === 'LOCAL_RESIDENT' && group.homeDestination?.coordinates
      ? {
          coordinates: group.homeDestination.coordinates,
          ...(group.homeDestination.label ? { address: group.homeDestination.label } : {}),
        }
      : null;

  const openGroupDispatch = (): void => {
    setDispatchError(null);
    setDispatchIntent({ passengerUrns: [], leg: null, key: `group:${Date.now()}` });
  };

  const openRideHome = (): void => {
    setDispatchError(null);
    setDispatchIntent({ passengerUrns: [], leg: 'AIRPORT_TO_HOME', key: `home:${Date.now()}` });
  };

  const openPassengerDispatch = (paxUrn: string, leg: TripLeg | null = null): void => {
    setDispatchError(null);
    setDispatchIntent({ passengerUrns: [paxUrn], leg, key: `pax:${paxUrn}:${Date.now()}` });
  };

  const confirmDispatch = async (input: DispatchDialogSubmit): Promise<void> => {
    setDispatchError(null);
    try {
      // Stamp the trip with its PNR + disruption. Without the disruption hint
      // the desk's `GET /v1/disruptions/:urn/trips` can only find the trip via
      // the transfer-demand caseUrns fallback — which excludes no-hotel PNRs.
      const disruptionUrn = routingDisruptionUrn(scope, group);
      // A home ride names its two ends: the declared home point as the drop-off
      // and the airport the passenger is stranded at as the pickup — the overnight
      // station when the night falls at a hub, else the disrupted flight's origin.
      const home = input.leg === 'AIRPORT_TO_HOME' && homeDropoff ? homeDropoff : null;
      await dispatch.mutateAsync({
        caseUrn: group.caseUrn,
        subCaseUrn: group.subCaseUrn,
        ...(disruptionUrn ? { disruptionUrn } : {}),
        passengerUrns: input.passengerUrns,
        leg: input.leg,
        reason: input.reason,
        ...(input.scheduledAt ? { scheduledAt: input.scheduledAt } : {}),
        ...(home ? { dropoff: home, airportIata: group.overnightStation ?? group.origin } : {}),
      });
      setDispatchIntent(null);
    } catch (e) {
      setDispatchError(dispatchRefusal(e, t) ?? errorMessage(e, t('transport.dispatchFailed')));
    }
  };

  const confirmCancel = async (reason: string): Promise<void> => {
    if (!cancelTarget) return;
    setCancelError(null);
    try {
      await cancel.mutateAsync({ urn: cancelTarget.urn, reason });
      setCancelTarget(null);
    } catch (e) {
      setCancelError(errorMessage(e, t('transport.cancelFailed')));
    }
  };

  return (
    <Card data-testid="drawer-transport">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 p-4 pb-2">
        <CardTitle className={cn('flex items-center gap-1.5', FIELD_LABEL_CLASS)}>
          <Bus className="h-3.5 w-3.5" aria-hidden /> {t('transport.title')}
          {mine.length > 0 ? (
            <span className="tabular-nums text-muted-foreground/80">({mine.length})</span>
          ) : null}
        </CardTitle>
        {canDispatch ? (
          <div className="flex flex-wrap items-center gap-2">
            {homeDropoff ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={openRideHome}
                title={t('transport.rideHomeHint')}
                data-testid="transport-ride-home-open"
              >
                <Home className="h-3.5 w-3.5" aria-hidden />
                {t('transport.rideHome')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openGroupDispatch}
              data-testid="transport-dispatch-open"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden />
              {t('transport.dispatch')}
            </Button>
          </div>
        ) : null}
      </CardHeader>

      <CardContent className="space-y-3 p-4 pt-0 text-sm">
        {tripsQuery.isLoading ? (
          <div className="space-y-2" data-testid="transport-loading" aria-busy="true">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : tripsQuery.isError ? (
          <div className="space-y-2" data-testid="transport-error" role="alert">
            <p className="flex items-start gap-1.5 text-xs text-destructive">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span>{t('transport.loadError')}</span>
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => void tripsQuery.refetch()}
              data-testid="transport-retry"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              {t('transport.retry')}
            </Button>
          </div>
        ) : mine.length === 0 && !canDispatch ? (
          <div
            className="rounded-md border border-dashed border-border p-3 text-center"
            data-testid="transport-locked"
          >
            <p className="text-sm font-medium">{t('transport.lockedTitle')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {hotelPending ? t('transport.lockedBodyHotelPending') : t('transport.lockedBody')}
            </p>
          </div>
        ) : mine.length === 0 && carried.size > 0 ? (
          // No ride ordered, and none needed: the voucher below IS the transport.
          <p className="text-xs text-muted-foreground" data-testid="transport-voucher-is-transport">
            {t('transport.voucherIsTransport')}
          </p>
        ) : mine.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-border p-3 text-center"
            data-testid="transport-empty"
          >
            <p className="text-sm font-medium">{t('transport.emptyTitle')}</p>
            <p className="mt-0.5 text-xs text-muted-foreground" data-testid="transport-empty-body">
              {destination === 'HOME'
                ? t('transport.emptyBodyHome')
                : transportMode === 'AUTOMATIC'
                  ? t('transport.emptyBodyAutomatic')
                  : transportMode === 'ON_DEMAND'
                    ? t('transport.emptyBodyOnDemand')
                    : t('transport.emptyBody')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border" data-testid="transport-trips">
            {mine.map((trip) => (
              <TripRow
                key={trip.urn}
                trip={trip}
                passengerLabel={nameOf(trip.passengerUrn)}
                airportIata={group.origin}
                coveredByVoucher={voucherCovers(trip)}
                onCancel={() => {
                  setCancelError(null);
                  setCancelTarget(trip);
                }}
                onRedispatch={canDispatch ? () => openPassengerDispatch(trip.passengerUrn, trip.leg) : undefined}
              />
            ))}
          </ul>
        )}

        {/* Per-passenger dispatch — only for passengers with nothing on the way. */}
        {canDispatch && !tripsQuery.isLoading && !tripsQuery.isError && withoutTransport.length > 0 ? (
          <div className="space-y-1" data-testid="transport-pax-without">
            <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t('transport.withoutTransport', { n: withoutTransport.length })}
            </p>
            <ul className="flex flex-wrap gap-1.5">
              {withoutTransport.map((p) => (
                <li key={p.paxUrn}>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 px-2 text-xs"
                    title={p.paxUrn}
                    onClick={() => openPassengerDispatch(p.paxUrn)}
                    data-testid={`transport-dispatch-pax-open-${p.paxUrn}`}
                  >
                    <Plus className="h-3 w-3" aria-hidden />
                    <span className="max-w-[12rem] truncate">{p.fullName}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* The rest of the case's rides — other PNR groups. Collapsed: this drawer is
            about one PNR, but an operator working a family across two bookings should
            not have to close it to see the other half. */}
        {others.length > 0 ? (
          <details className="rounded-md border border-border" data-testid="transport-other-trips">
            <summary className="cursor-pointer px-2.5 py-1.5 text-xs text-muted-foreground">
              {t('transport.otherRides', { n: others.length })}
            </summary>
            <ul className="divide-y divide-border border-t border-border">
              {others.map((trip) => (
                <TripRow
                  key={trip.urn}
                  trip={trip}
                  passengerLabel={nameOf(trip.passengerUrn)}
                  airportIata={group.origin}
                  compact
                  coveredByVoucher={voucherCovers(trip)}
                  onCancel={() => {
                    setCancelError(null);
                    setCancelTarget(trip);
                  }}
                  onRedispatch={canDispatch ? () => openPassengerDispatch(trip.passengerUrn, trip.leg) : undefined}
                />
              ))}
            </ul>
          </details>
        ) : null}

        {/* The group's Uber voucher (#797) — renders only once a program exists; the
            claim/redeem badges ride the TransportVoucherChanged push. */}
        {canDispatch && group.subCaseUrn ? (
          <TransportVoucherSection
            caseUrn={group.caseUrn}
            subCaseUrn={group.subCaseUrn}
            disruptionUrn={routingDisruptionUrn(scope, group)}
            passengers={group.passengers.map((p) => ({ paxUrn: p.paxUrn, fullName: p.fullName }))}
            airportIata={group.overnightStation ?? group.origin}
            hotelRef={
              group.hotelReservationUrn && group.hotelUrn
                ? { reservationUrn: group.hotelReservationUrn, hotelUrn: group.hotelUrn }
                : null
            }
            home={
              homeDropoff
                ? {
                    // GeoJSON `[longitude, latitude]` → the fence's named axes.
                    latitude: homeDropoff.coordinates[1],
                    longitude: homeDropoff.coordinates[0],
                    ...(homeDropoff.address ? { address: homeDropoff.address } : {}),
                  }
                : null
            }
          />
        ) : null}

        {/* The pre-existing info line: the fare's transport ESTIMATE, which is not
            what these rides cost. Kept, demoted to a footnote, testids unchanged —
            and only once there is a destination to estimate a ride to. */}
        {canDispatch ? (
          <p
            className="flex items-center justify-between gap-2 border-t border-border pt-2 text-xs text-muted-foreground"
            title={t('transport.estimateHint')}
          >
            <span>{t('transport.estimateLabel')}</span>
            {typeof transportPriceUsd === 'number' && transportPriceUsd > 0 ? (
              <span className="font-medium tabular-nums" data-testid="drawer-transport-included">
                ${transportPriceUsd.toLocaleString()}
              </span>
            ) : transportPriceUsd === null && hasSelected ? (
              <span data-testid="drawer-transport-unpriced">{t('transport.estimateUnavailable')}</span>
            ) : (
              <span data-testid="drawer-transport-none">
                {hasSelected ? t('transport.noneNeeded') : '—'}
              </span>
            )}
          </p>
        ) : null}

        {/* transport-desk (T4): jump to the disruption's Transport desk with THIS
            PNR pre-selected in the exceptions queue. Only when the row knows its
            parent disruption — a standalone case has no desk to open. */}
        {canDispatch && group.disruptionUrn ? (
          <p className="text-xs">
            <Link
              to={`${towerPath(group.disruptionUrn)}#transport?pnr=${encodeURIComponent(group.subCaseUrn)}`}
              className="font-medium text-primary hover:underline"
              data-testid="transport-open-desk"
            >
              {t('transport.openDesk')}
            </Link>
          </p>
        ) : null}
      </CardContent>

      {dispatchIntent ? (
        <TransportDispatchDialog
          key={dispatchIntent.key}
          groupLabel={group.pnrLabel}
          passengers={group.passengers.map((p) => {
            const live = liveTripFor(mine, p.paxUrn);
            return {
              paxUrn: p.paxUrn,
              fullName: p.fullName,
              ...(live ? { liveStatus: live.status } : {}),
            };
          })}
          initialPassengerUrns={dispatchIntent.passengerUrns}
          initialLeg={dispatchIntent.leg}
          allowHome={homeDropoff ? { label: group.homeDestination?.label } : null}
          pending={dispatch.isPending}
          error={dispatchError}
          onOpenChange={(open) => {
            if (!open) setDispatchIntent(null);
          }}
          onConfirm={(input) => void confirmDispatch(input)}
        />
      ) : null}

      {cancelTarget ? (
        <TransportCancelDialog
          key={cancelTarget.urn}
          trip={cancelTarget}
          passengerLabel={nameOf(cancelTarget.passengerUrn)}
          pending={cancel.isPending}
          error={cancelError}
          onOpenChange={(open) => {
            if (!open) setCancelTarget(null);
          }}
          onConfirm={(reason) => void confirmCancel(reason)}
        />
      ) : null}
    </Card>
  );
}

/**
 * One trip. Status is the headline (an operator scanning the card wants to know
 * "is a car coming?" before anything else); the driver/vehicle/plate line is what
 * they read out to the passenger — with the vehicle colour folded in and the
 * pickup ETA on its own row when the vendor reports them (`TripView.driver@v1`,
 * additive: Uber does, the mock does not, and then the row reads exactly as it
 * did before); the URN is secondary, on the row's `title`.
 */
function TripRow({
  trip,
  passengerLabel,
  airportIata,
  compact = false,
  coveredByVoucher = false,
  onCancel,
  onRedispatch,
}: {
  trip: TripView;
  passengerLabel: string;
  airportIata?: string | undefined;
  compact?: boolean;
  /** The group's Uber voucher pays this ride: a failure is history, never a re-dispatch. */
  coveredByVoucher?: boolean;
  onCancel: () => void;
  /** Absent while the PNR has no destination — a re-dispatch IS a dispatch. */
  onRedispatch?: (() => void) | undefined;
}) {
  const { t } = useTranslation('pnrActions');
  const presentation = TRIP_STATUS_PRESENTATION[trip.status];
  // Only a ride nobody else pays for is the operator's to retry.
  const redispatchable = isRedispatchable(trip) && !coveredByVoucher;
  const driver = trip.driverInfo;
  // The colour is part of how the vehicle reads out to the passenger ("white
  // Toyota Corolla"), so it folds into the vehicle text rather than taking a
  // separator of its own — and without a vehicle to describe it says nothing.
  const vehicle = driver?.vehicle
    ? [driver.vehicleColor, driver.vehicle].filter(Boolean).join(' ')
    : undefined;
  const driverLine = driver
    ? [driver.name, vehicle, driver.licensePlate].filter(Boolean).join(' · ')
    : null;
  // Minutes to pickup as the vendor last reported it. Once the trip is terminal
  // the number is history however recent it is, so it is not shown at all.
  const etaMinutes =
    trip.pickupEtaMinutes != null && !isTerminalTripStatus(trip.status)
      ? Math.max(0, Math.round(trip.pickupEtaMinutes))
      : null;

  return (
    <li
      className={compact ? 'px-2.5 py-2' : 'py-2.5 first:pt-0 last:pb-0'}
      data-testid="transport-trip"
      data-trip-urn={trip.urn}
      data-trip-status={trip.status}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant={presentation.tone} size="sm" data-testid="transport-trip-status">
              {t(presentation.labelKey)}
            </Badge>
            <span className="text-xs font-medium">{t(TRIP_LEG_LABEL_KEY[trip.leg])}</span>
            <span className="text-[11px] text-muted-foreground" title={trip.urn}>
              {providerLabel(trip.providerName)} · {t(TRIP_DISPATCH_MODE_LABEL_KEY[trip.dispatchMode])}
            </span>
          </div>

          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <UserRound className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate" title={trip.passengerUrn}>
              {passengerLabel}
            </span>
          </p>

          {driverLine ? (
            <p
              className="flex items-center gap-1.5 text-xs"
              data-testid="transport-trip-driver"
            >
              <CarFront className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
              <span className="truncate">{driverLine}</span>
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">{t(presentation.hintKey)}</p>
          )}

          {trip.scheduledAt ? (
            <p
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid="transport-trip-scheduled"
            >
              <Clock className="h-3 w-3 shrink-0" aria-hidden />
              {t('transport.pickup', { time: formatFlightTime(trip.scheduledAt, airportIata) })}
            </p>
          ) : null}

          {etaMinutes !== null ? (
            <p
              className="flex items-center gap-1.5 text-xs tabular-nums"
              data-testid="transport-trip-eta"
            >
              <Timer className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
              {t('transport.eta', { minutes: etaMinutes })}
            </p>
          ) : null}

          {/* `lastError` is the producer's last word on the trip — a vendor failure on
              FAILED, but on CANCELLED it is just the operator's own cancel note. Only
              the first is an error, so only the first is coloured like one. */}
          {trip.lastError ? (
            <p
              className={
                redispatchable
                  ? 'flex items-start gap-1.5 text-xs text-destructive'
                  : 'flex items-start gap-1.5 text-xs text-muted-foreground'
              }
              data-testid="transport-trip-error"
            >
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              <span>{trip.lastError}</span>
            </p>
          ) : null}

          {coveredByVoucher && isTerminalTripStatus(trip.status) ? (
            <p
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
              data-testid="transport-trip-voucher-covers"
            >
              <Ticket className="h-3 w-3 shrink-0" aria-hidden />
              {t('transport.voucherCoversRide')}
            </p>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1">
          {/* The airline's price of the ride (operator-bff converts Nexa's cost
              server-side, commercial-contract P5): absent until the vendor writes
              an amount; `null` = could not be priced → said, never guessed. */}
          {trip.price !== undefined ? (
            <span
              className={cn(
                'tabular-nums',
                trip.price ? 'text-sm font-medium' : 'text-xs text-muted-foreground',
              )}
              data-testid="transport-trip-price"
              title={
                trip.price
                  ? t('transport.tripPrice', { amount: formatPrice(trip.price.amount, trip.price.currency) })
                  : undefined
              }
            >
              {trip.price
                ? formatPrice(trip.price.amount, trip.price.currency)
                : t('transport.tripPriceUnavailable')}
            </span>
          ) : null}
          <div className="flex gap-1">
            {onRedispatch && redispatchable ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={onRedispatch}
                data-testid="transport-redispatch"
              >
                <RotateCcw className="h-3 w-3" aria-hidden />
                {t('transport.redispatch')}
              </Button>
            ) : null}
            {isCancellable(trip) ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs"
                onClick={onCancel}
                data-testid="transport-cancel-open"
              >
                <XCircle className="h-3 w-3" aria-hidden />
                {t('common:common.cancel')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

function formatPrice(amount: number, currency: string): string {
  try {
    return amount.toLocaleString(undefined, { style: 'currency', currency });
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
}

/** Last URN segment — a readable fallback when a trip names a pax off this roster. */
function shortUrn(urn: string): string {
  const parts = urn.split(':');
  return parts[parts.length - 1] || urn;
}

/**
 * The domain's own "no" to a dispatch, in the operator's words: a ride the group's Uber
 * voucher already pays (409 `VOUCHER_COVERS_GROUP` — cancel the voucher first) or a
 * deployment with no car vendor (409 `NO_CAR_VENDOR` — the voucher is the transport).
 */
function dispatchRefusal(error: unknown, t: (key: string) => string): string | null {
  const code = (error as { code?: unknown } | null)?.code;
  if (code === 'VOUCHER_COVERS_GROUP') return t('transport.refusal.voucherCoversGroup');
  if (code === 'NO_CAR_VENDOR') return t('transport.refusal.noCarVendor');
  return null;
}

function isInfant(paxType: string | undefined): boolean {
  const type = (paxType ?? '').toUpperCase();
  return type === 'INF' || type === 'INFANT';
}

function errorMessage(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : '';
  return message && message.length < 200 ? message : fallback;
}
